// review_card.tsx

import ArticleCard from "@/components/common/cards/article_card";
import Typography from "@/components/common/typography";
import VideoModal from "@/components/common/ui/video_modal/videoModal";
import { FiveStar } from "@/components/icons/stars/fiveStar";
import { VideoPlayIcon } from "@/components/icons/videos";
import { BlockReviewsCard, ComponentVideoCard } from "@/types/schema";
import { DirectusFile } from "@directus/sdk";
import Image from "next/image";
import { useState } from "react";

// New type for the populated review images
export type BlockReviewsCardFilePopulated = {
  id: number;
  directus_files_id: DirectusFile;
};

// Define the type for the written review card props
type WrittenReviewCardProps = BlockReviewsCard & {
  videoMode: false;
};

// Define the type for the video review card props
type VideoReviewCardProps = ComponentVideoCard & {
  videoMode: true;
};

// Use a discriminated union for the props
type ReviewCardProps = WrittenReviewCardProps | VideoReviewCardProps;

const ReviewCard = (props: ReviewCardProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };
  // Video Review Card
  if (props.videoMode) {
    const { video_cover, video_link } = props;
    const coverFile = video_cover as unknown as DirectusFile;
    return (
  <article className="flex rounded-lg flex-col gap-3.5 max-w-[19.0625rem] lg:min-w-[19.0625rem] min-w-[14.0625rem] max-h-[33.875rem] overflow-hidden relative">
    <div
      className="relative w-full h-full cursor-pointer" 
      onClick={openModal} 
    >
      <Image
        width={500}
        height={500}
        src={coverFile.filename_disk!}
        alt="Image Cover for Video Review"
        className="object-cover w-full h-full "
      />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
        <VideoPlayIcon />
      </div>
    </div>

    {video_link && (
      <VideoModal
        isOpen={isModalOpen}
        onClose={closeModal}
        videoLink={video_link}
      />
    )}
  </article>
);
  }

  // Written Review Card
  const {
    review_images,
    review_date,
    reviewer_photo,
    rating,
    reviewer_name,
    review_description,
  } = props;
  const imagesArray =
    review_images as unknown as BlockReviewsCardFilePopulated[];
  const reviewerPhoto = reviewer_photo as unknown as DirectusFile;

  return (
    <article className="flex rounded-lg px-5 pb-5 pt-5 shadow-default flex-col gap-3.5 max-w-[39.375rem] lg:min-w-[39.375rem] min-w-full h-full">
      <header className="flex items-center gap-4">
        <Image
          width={45}
          height={45}
          src={reviewerPhoto.filename_disk!}
          alt="Reviewer photo"
          className="rounded-full"
        />
        <Typography size="body1">{reviewer_name}</Typography>
      </header>

      <span className="flex items-center gap-2.5">
        <FiveStar />
        <Typography size="body2" color="darkGray">
          {review_date}
        </Typography>
      </span>

      <blockquote className="min-h-28 overflow-hidden">
        <Typography size="review" className="line-clamp-5">
          {review_description}
        </Typography>
      </blockquote>

      <figure className="flex flex-row gap-2.5 max-h-24 overflow-x-scroll no-scrollbar">
        {imagesArray?.map((image, idx) => (
          <Image
            key={idx}
            src={image.directus_files_id.filename_disk!}
            alt="Review Card"
            className="rounded-sm object-cover"
            width={106}
            height={106}
          />
        ))}
      </figure>
    </article>
  );
};

export default ReviewCard;
